import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {RepoIcon} from '@primer/octicons-react'
import {Label, Link} from '@primer/react'
import {clsx} from 'clsx'
import type {FC} from 'react'

import {LABELS} from '../../constants/labels'
import type {RepositoryPillRepositoryNode, RepositoryPillRepositoryOwnerNode} from './repository-pill-query-types'
import styles from './RepositoryPill.module.css'

type RepositoryPillFragment = {
  readonly repository: Pick<RepositoryPillRepositoryNode, 'name' | 'isPrivate'> & {
    readonly owner: Pick<RepositoryPillRepositoryOwnerNode, 'login' | '__typename'>
  }
}

export type RepositoryPillProps = {
  repositoryPillData: RepositoryPillFragment
  className?: string
  isSmall?: boolean
}

export const SmallRepositoryPill: FC<RepositoryPillProps> = props => <RepositoryPill {...props} isSmall />

export function RepositoryPill({repositoryPillData, className, isSmall = false}: RepositoryPillProps) {
  const {repository} = repositoryPillData
  const repoOwner = repository.owner.login
  const repoName = repository.name
  const isOrganization = repository.owner.__typename === 'Organization'
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = isOrganization
    ? getHovercardAttributesForType('organization', {login: repoOwner})
    : getHovercardAttributesForType('user', {login: repoOwner})

  const link = (
    <div className={clsx(styles.repositoryInfoContainer, !isSmall && styles.repositoryInfoContainerLarge)}>
      {!isSmall && <RepoIcon className={styles.repositoryIcon} />} {/* 2px to achieve visual alignment */}
      <Link href={`/${repoOwner}`} {...hovercardAttrs} className={styles.ownerLink}>
        {repoOwner}
      </Link>
      <span>/</span>
      <Link href={`/${repoOwner}/${repoName}`} className={styles.repositoryNameLink}>
        {repoName}
      </Link>
    </div>
  )

  if (isSmall) {
    return link
  }

  return (
    <div className={clsx(styles.smallRepositoryPill, className)}>
      {link}
      <Label size="small" data-watch-overflow className={styles.repositoryLabel}>
        {repository.isPrivate ? LABELS.private : LABELS.public}
      </Label>
    </div>
  )
}
